// @ts-nocheck
import { Calendar, ChevronDown, ChevronUp } from "lucide-react";
import React, { useEffect, useState } from "react";

import DiarioComentariosPanel from "@/features/diario/components/DiarioComentariosPanel";
import { useRouteRefresh } from "@/features/sistema/hooks/useRouteRefresh";
import { supabase } from "@/features/sistema/services/supabaseClient";

type Subetapa = {
  id: string;
  obra_id: string;
  nome: string;
  ativo: boolean;
  ordem: number;
  data_prevista_inicio: string | null;
  data_prevista_fim: string | null;
  data_inicio_real: string | null;
  data_fim_real: string | null;
  status: 'nao_iniciado' | 'em_andamento' | 'parado' | 'concluido';
};

const formatDateBR = (d?: string | null) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isFinite(dt.getTime()) ? dt.toLocaleDateString("pt-BR") : "—";
};

export default function ClienteCronograma({ obraId }: { obraId: string }) {
  const [subetapas, setSubetapas] = useState<Subetapa[]>([]);
  const [loading, setLoading] = useState(false);
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});

  useRouteRefresh(() => {
    if (!obraId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await supabase
          .from("subetapas_obra")
          .select("id, obra_id, nome, ativo, ordem, data_prevista_inicio, data_prevista_fim, data_inicio_real, data_fim_real, status")
          .eq("obra_id", obraId)
          .eq("ativo", true);

        // Defensivo: obra correta, ativas, e com datas válidas (início e fim após fallback real→prevista)
        const base: Subetapa[] = (data ?? []).filter((r: any) =>
          r.obra_id === obraId &&
          r.ativo === true &&
          (r.data_inicio_real || r.data_prevista_inicio) &&
          (r.data_fim_real || r.data_prevista_fim)
        );

        // Ordenação cronológica: início real → previsto → fim real → fim previsto
        const ts = (s: Subetapa) =>
          Date.parse(s.data_inicio_real || s.data_prevista_inicio || s.data_fim_real || s.data_prevista_fim || "") ||
          Number.MAX_SAFE_INTEGER;
        base.sort((a, b) => ts(a) - ts(b) || a.ordem - b.ordem);

        setSubetapas(base);
      } finally {
        setLoading(false);
      }
    })();
  });

  const toggleOpen = (id: string) =>
    setOpenIds(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <section className="rounded-2xl border border-secondary bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-ageo-bold text-foreground">Cronograma</h2>
      </div>

      {loading && <div className="text-sm text-gray-500">Carregando…</div>}
      {!loading && subetapas.length === 0 && (
        <div className="text-sm text-gray-500">Nenhuma subetapa cadastrada.</div>
      )}

      <div className="divide-y divide-secondary/60">
        {subetapas.map((s) => {
          const isOpen = !!openIds[s.id];
          return (
            <div key={s.id} className="py-3">
              <button
                type="button"
                onClick={() => toggleOpen(s.id)}
                className="w-full grid grid-cols-1 md:grid-cols-12 items-center gap-2 text-left"
                aria-expanded={isOpen}
              >
                <div className="md:col-span-5 flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-secondary/80 text-foreground">
                    {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </span>
                  <span className="font-ageo-bold text-foreground">{s.nome}</span>
                </div>
                <div className="md:col-span-3 flex items-center gap-1 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>Início: {formatDateBR(s.data_inicio_real || s.data_prevista_inicio)}</span>
                </div>
                <div className="md:col-span-3 flex items-center gap-1 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>Fim: {formatDateBR(s.data_fim_real || s.data_prevista_fim)}</span>
                </div>
                <div className="md:col-span-1">
                  <span className="inline-block px-2 py-0.5 rounded-full border border-secondary text-xs text-foreground">
                    {s.status === 'nao_iniciado'
                      ? 'Não iniciado'
                      : s.status === 'em_andamento'
                      ? 'Em andamento'
                      : s.status === 'parado'
                      ? 'Parado'
                      : s.status === 'concluido'
                      ? 'Concluído'
                      : '—'}
                  </span>
                </div>
              </button>

              {isOpen && (
                <div className="mt-3">
                  <DiarioComentariosPanel
                    obraId={obraId}
                    resource="cronograma_subetapa"
                    resourceId={s.id}
                    dark={false}
                    tone="white"
                    allowCreate={true}
                    allowEdit={true}
                    allowDelete={true}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
